import { forEach, isNaN } from 'lodash-es';
import {
  log,
  TIME_DASHBOARD_TOGGLE_STORAGE_KEY as LOCAL_STORAGE_KEY,
  ToggleStatus,
} from '@unanet/sdk';
import { context } from '#/lib/constants';
import { type TimeDashboardToggleWebComponentProps } from './models';

const _getLocalStorageData = (): Record<number, ToggleStatus> => {
  try {
    const stringItem = localStorage.getItem(LOCAL_STORAGE_KEY);
    const data: Record<number, ToggleStatus> = stringItem
      ? JSON.parse(stringItem)
      : {};
    return data;
  } catch (e) {
    log.warn({
      msg: 'Error getting time dashboard toggle local storage',
      error: `${e}`,
    });
    return {};
  }
};

/**
 * @desc reset all ignore-for-session flags in local storage. NOTE: this will reset ALL ignore-for-session flags.
 */
export const cleanUpIgnoreFlags = (isOnReact: boolean) => {
  try {
    const data = _getLocalStorageData();
    const resetData = { ...data };

    if (Object.keys(data).length === 0) {
      return;
    }

    forEach(data, (item, personKey) => {
      if (!item.match(/opted/)) {
        resetData[personKey] = isOnReact
          ? ToggleStatus.OptedIn
          : ToggleStatus.OptedOut;
      }
    });

    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(resetData));
  } catch (e) {
    log.warn({
      msg: 'Error resetting time dashboard toggle local storage',
      error: `${e}`,
    });

    localStorage.removeItem(LOCAL_STORAGE_KEY);
  }
};

/**
 * @desc get the person's dashboard status from local storage.
 * @param personKey
 */
export const getPersonStorageStatus = ({
  personKey,
}: {
  personKey: number;
}) => {
  const data = _getLocalStorageData();
  return data[personKey];
};

/**
 * @desc parse the props passed in from the web component. They will always be strings
 * so we will convert them to meaningful types here.
 * @param TimeDashboardToggleWebComponentProps
 */

export const parseProps = ({
  hideAndReset: hideAndResetString,
  personKey: personKeyString,
}: TimeDashboardToggleWebComponentProps) => {
  const hideAndReset = hideAndResetString?.toLowerCase() === 'true';
  const parsedKey = Number(personKeyString);
  const personKey = !isNaN(parsedKey) ? parsedKey : -1;

  return {
    hideAndReset,
    personKey,
  };
};

/**
 * @desc remove all dashboard toggles from local storage
 */
export const resetFlags = () => {
  localStorage.removeItem(LOCAL_STORAGE_KEY);
};

/**
 * @desc set the person's dashboard status in local storage
 * @param personKey - current logged in user's person key. This the key for storing their status in local storage.
 * @param toggleStatus - current logged in user's dashboard toggle status to be stored.
 */
export const setPersonStorageItem = ({
  personKey,
  toggleStatus,
}: {
  personKey: number;
  toggleStatus: ToggleStatus;
}) => {
  try {
    const data = { ..._getLocalStorageData() };
    data[personKey] = toggleStatus;

    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    log.warn({
      msg: 'Error setting time dashboard toggle local storage',
      error: `${e}`,
    });
  }
};

/**
 * @desc navigate to the legacy web app time dashboard
 */
export const toLegacyTimeDashboard = () => {
  if (localStorage.getItem('una-disableredirect')) {
    log.info({
      msg: 'Redirect to legacy time dashboard disabled by una-disableredirect localStorage key',
      url: `/${context}/action/time`,
    });
    return;
  }
  window.location.href = `/${context}/action/time`;
};

/**
 * @desc navigate to the new React time dashboard
 */
export const toReactTimeDashboard = () => {
  if (localStorage.getItem('una-disableredirect')) {
    log.info({
      msg: 'Redirect to React time dashboard disabled by una-disableredirect localStorage key',
      url: `/${context}/spa_action/time`,
    });
    return;
  }
  window.location.href = `/${context}/spa_action/time`;
};
